import { TranscriptParagraph } from "../../../shared/interfaces/Transcript";

/** "M:SS" — the display format used across the transcript + video UI. */
export const formatTimestamp = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/** "M:SS.cc" — centisecond precision for marker placement / range readouts. */
export const formatPreciseTimestamp = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  const cs = Math.round((safe - Math.floor(safe)) * 100);
  return `${mins}:${secs.toString().padStart(2, "0")}.${cs
    .toString()
    .padStart(2, "0")}`;
};

/**
 * Parses a user-typed timestamp into seconds. Accepts "SS", "SS.cc", "M:SS",
 * "MM:SS.cc", or "H:MM:SS". Returns null if it can't be parsed.
 */
export const parseTimestamp = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":");
  if (parts.length > 3) return null;
  if (parts.some((p) => p === "" || !/^\d*\.?\d*$/.test(p) || Number.isNaN(Number(p))))
    return null;
  const nums = parts.map(Number);
  let seconds: number;
  if (nums.length === 1) seconds = nums[0];
  else if (nums.length === 2) seconds = nums[0] * 60 + nums[1];
  else seconds = nums[0] * 3600 + nums[1] * 60 + nums[2];
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  return seconds;
};

/** Word count by whitespace splitting; empty/whitespace → 0. */
export const countWords = (text: string): number => {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
};

/** An entry's full text = its sentences joined by a space. */
export const paragraphText = (paragraph: TranscriptParagraph): string =>
  paragraph.sentences.map((s) => s.text).join(" ");

/** Word count of an entry (sum across its sentences). */
export const paragraphWordCount = (paragraph: TranscriptParagraph): number =>
  countWords(paragraphText(paragraph));
